import { useTranslation } from 'react-i18next';
import { useState, type FC } from 'react';
import {
  SMS_ACTIVITIES_PER_PAGE,
  useGetSmsActivities,
} from 'infrastructure/hooks/ccm';
import BaseTableNew from 'components/atoms/base-table/new';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import EmptyClinicalSmsIcon from 'components/atoms/icons/emptyClinicalSmsIcon';
import { ModuleType } from 'infrastructure/enums';
import classNames from 'classnames';
import { useModule } from 'infrastructure/providers/module-provider';

import s from './styles.module.scss';
import { columns } from './columns';
import ModuleTabs from '../module-tabs';

import type { SortState } from 'infrastructure/types';

interface SmsActivityLoggedProps {
  patientGuid: string;
  showTabs?: boolean;
  module?: ModuleType;
}

const SmsActivityLogged: FC<SmsActivityLoggedProps> = ({
  patientGuid,
  showTabs,
  module = ModuleType.CCM,
}) => {
  const [page, setPage] = useState(1);
  const [documentationTimeSort, setDocumentationTimeSort] =
    useState<SortState>('');
  const { module: moduleType, changeModule: setModuleType } = useModule(module);

  const { smsActivity, isLoading } = useGetSmsActivities({
    patientGuid,
    meta: { items: SMS_ACTIVITIES_PER_PAGE, page },
    module: moduleType,
    documentationTimeSort,
  });
  const { t } = useTranslation();

  return (
    <div className={s.wrapper}>
      <div className={classNames({ [s['title-wrapper']]: showTabs })}>
        <h3 className={s.title} data-cy="sms-activity-section">
          {t('labels.smsActivityLogged')}
        </h3>
        {showTabs && (
          <ModuleTabs
            activeKey={moduleType}
            className={s.tabs}
            onChange={(tab) => {
              setModuleType(tab.key);
              setPage(1);
            }}
          />
        )}
      </div>
      <BaseTableNew
        items={smsActivity?.items ?? []}
        columns={columns({ documentationTimeSort })}
        bordered
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: SMS_ACTIVITIES_PER_PAGE,
          total: smsActivity?.count ?? 0,
          position: ['bottomCenter'],
        }}
        onChangePage={(newPage) => {
          setPage(newPage);
        }}
        dataCy="sms-activity-table"
        emptyText={
          <EmptyTablePlaceholder
            icon={<EmptyClinicalSmsIcon />}
            text={t('labels.noSMSActivityLogged')}
          />
        }
        onSort={(field, order) => {
          setDocumentationTimeSort(order);
        }}
      />
    </div>
  );
};
export default SmsActivityLogged;
